import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ShoppingBag, ArrowRight, X, MessageCircle } from "lucide-react";
import { demoCategories, demoRestaurant } from "@/data/demoMenu";
import demoCover from "@/assets/demo-cover.jpg";
import demoLogo from "@/assets/demo-logo.png";

interface Phase {
  at: number;
  catIdx: number;
  tapped: string | null;
  cart: { count: number; total: number };
  cartTapped?: boolean;
  showCheckout?: boolean;
  checkoutBtnTapped?: boolean;
}

const PHASES: Phase[] = [
  { at: 0,     catIdx: 0, tapped: null,          cart: { count: 0, total: 0 } },
  { at: 2500,  catIdx: 0, tapped: "demo-item-1", cart: { count: 0, total: 0 } },
  { at: 3800,  catIdx: 0, tapped: null,          cart: { count: 1, total: 32.9 } },
  { at: 5800,  catIdx: 1, tapped: null,          cart: { count: 1, total: 32.9 } },
  { at: 7800,  catIdx: 1, tapped: "demo-item-2", cart: { count: 1, total: 32.9 } },
  { at: 9100,  catIdx: 1, tapped: null,          cart: { count: 2, total: 73.4 } },
  { at: 10600, catIdx: 1, tapped: null,          cart: { count: 2, total: 73.4 }, cartTapped: true },
  { at: 11400, catIdx: 1, tapped: null,          cart: { count: 2, total: 73.4 }, cartTapped: false, showCheckout: true },
  { at: 13400, catIdx: 1, tapped: null,          cart: { count: 2, total: 73.4 }, showCheckout: true, checkoutBtnTapped: true },
  { at: 14200, catIdx: 1, tapped: null,          cart: { count: 2, total: 73.4 }, showCheckout: true, checkoutBtnTapped: false },
];

const LOOP_MS = 17000;

const ORDER_ITEMS = [
  { name: "Burger Artesanal", qty: 1, price: 32.9 },
  { name: "Pizza Margherita", qty: 1, price: 40.5 },
];

function formatPrice(v: number) {
  return v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function AnimatedMenuPreview() {
  const [activeCatIdx, setActiveCatIdx] = useState(0);
  const [tappedItemId, setTappedItemId] = useState<string | null>(null);
  const [cart, setCart] = useState({ count: 0, total: 0 });
  const [cartTapped, setCartTapped] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutBtnTapped, setCheckoutBtnTapped] = useState(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const clear = () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };

    const run = () => {
      clear();
      for (const phase of PHASES) {
        const t = setTimeout(() => {
          setActiveCatIdx(phase.catIdx);
          setTappedItemId(phase.tapped);
          setCart(phase.cart);
          setCartTapped(phase.cartTapped ?? false);
          setShowCheckout(phase.showCheckout ?? false);
          setCheckoutBtnTapped(phase.checkoutBtnTapped ?? false);
        }, phase.at);
        timersRef.current.push(t);
      }
      timersRef.current.push(setTimeout(run, LOOP_MS));
    };

    run();
    return clear;
  }, []);

  const activeCategory = demoCategories[activeCatIdx];

  return (
    <div className="relative overflow-hidden rounded-2xl border bg-background shadow-xl">
      {/* Cover */}
      <div className="relative h-36 w-full overflow-hidden sm:h-48">
        <img
          src={demoCover}
          alt=""
          className="h-full w-full object-cover"
          style={{ objectPosition: "center 55%" }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.35) 100%)" }}
        />
      </div>

      {/* Restaurant card */}
      <div className="relative mx-4 -mt-8 rounded-xl bg-card p-4 shadow-md ring-1 ring-black/5 sm:mx-6">
        <div className="flex items-center gap-3">
          <img src={demoLogo} alt="" className="h-12 w-12 rounded-lg object-cover ring-1 ring-black/5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-bold leading-tight sm:text-lg">{demoRestaurant.name}</h3>
              <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 ring-1 ring-emerald-200/60">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[10px] font-semibold text-emerald-700">Aberto</span>
              </span>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground truncate sm:text-sm">
              Culinária autoral · Jardins, SP
            </p>
          </div>
        </div>
      </div>

      {/* Category pills */}
      <div className="mt-4 flex gap-2 px-4 overflow-x-auto no-scrollbar sm:px-6">
        {demoCategories.map((cat, idx) => (
          <span
            key={cat.id}
            className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors duration-300 ${
              idx === activeCatIdx
                ? "bg-primary text-primary-foreground"
                : "bg-secondary text-secondary-foreground"
            }`}
          >
            {cat.name}
          </span>
        ))}
      </div>

      {/* Items grid */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeCategory.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:p-6"
        >
          {activeCategory.items.map((item) => {
            const isTapped = tappedItemId === item.id;
            const discountedPrice = item.discount_percent
              ? item.price * (1 - item.discount_percent / 100)
              : item.price;

            return (
              <motion.div
                key={item.id}
                animate={{ scale: isTapped ? 0.97 : 1 }}
                transition={{ duration: 0.15 }}
                className="group relative flex gap-3 overflow-hidden rounded-xl border bg-card p-3 shadow-sm sm:flex-col sm:gap-0 sm:p-0"
              >
                {(item.badge || item.discount_percent) && (
                  <div className="absolute top-2 left-2 z-10 hidden rounded-full bg-warm-gradient px-3 py-1 text-xs font-semibold text-primary-foreground shadow sm:block">
                    {item.badge ?? `-${item.discount_percent}%`}
                  </div>
                )}
                <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg sm:h-40 sm:w-full sm:rounded-none sm:rounded-t-xl">
                  {(item.badge || item.discount_percent) && (
                    <div className="absolute top-1.5 left-1.5 z-10 rounded-full bg-warm-gradient px-2 py-0.5 text-[10px] font-semibold text-primary-foreground shadow sm:hidden">
                      {item.badge ?? `-${item.discount_percent}%`}
                    </div>
                  )}
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col justify-center sm:p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="text-sm font-semibold leading-tight sm:text-base">{item.name}</h4>
                    <div className="shrink-0 text-right">
                      {item.discount_percent && (
                        <div className="text-[10px] line-through text-muted-foreground leading-none">
                          R${formatPrice(item.price)}
                        </div>
                      )}
                      <span className="text-sm font-bold text-primary sm:text-base">
                        R${formatPrice(discountedPrice)}
                      </span>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground line-clamp-2 sm:text-sm">
                    {item.description}
                  </p>
                  <motion.div
                    animate={isTapped ? { scale: 0.95, opacity: 0.75 } : { scale: 1, opacity: 1 }}
                    transition={{ duration: 0.12 }}
                    className="mt-2 flex h-8 w-full items-center justify-center gap-1 rounded-md bg-warm-gradient text-xs font-medium text-primary-foreground shadow-lg sm:mt-3 sm:h-9 sm:text-sm"
                  >
                    <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> Adicionar
                  </motion.div>
                </div>
                <AnimatePresence>
                  {isTapped && (
                    <motion.div
                      key="ripple"
                      initial={{ opacity: 0.5, scale: 0 }}
                      animate={{ opacity: 0, scale: 3 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.55, ease: "easeOut" }}
                      className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 h-10 w-10 rounded-full bg-primary/40"
                    />
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>
      </AnimatePresence>

      {/* Cart bar */}
      <AnimatePresence>
        {cart.count > 0 && !showCheckout && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0, scale: cartTapped ? 0.97 : 1 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.3 }}
            className="flex cursor-pointer items-center justify-center gap-2 border-t bg-warm-gradient px-5 py-3.5"
          >
            <ShoppingBag className="h-5 w-5 text-white" />
            <motion.span
              key={cart.count}
              initial={{ scale: 1.25 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
              className="font-semibold text-white"
            >
              {cart.count} {cart.count === 1 ? "item" : "itens"}
            </motion.span>
            <span className="mx-1 h-4 w-px bg-white/40" />
            <motion.span
              key={cart.total}
              initial={{ scale: 1.25 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
              className="font-bold text-white"
            >
              R${formatPrice(cart.total)}
            </motion.span>
            <ArrowRight className="ml-1 h-4 w-4 text-white" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Checkout drawer */}
      <AnimatePresence>
        {showCheckout && (
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-card shadow-[0_-8px_40px_rgba(0,0,0,0.18)] ring-1 ring-black/5"
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="h-1 w-10 rounded-full bg-muted-foreground/20" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <span className="text-base font-bold">Seu pedido</span>
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary">
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
            </div>

            {/* Items */}
            <div className="px-5 py-4 space-y-3">
              {ORDER_ITEMS.map((item, i) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08, duration: 0.25 }}
                  className="flex items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                      {item.qty}
                    </span>
                    <span className="text-sm font-medium">{item.name}</span>
                  </div>
                  <span className="text-sm font-semibold text-primary">R${formatPrice(item.price)}</span>
                </motion.div>
              ))}
            </div>

            {/* Total */}
            <div className="mx-5 flex items-center justify-between border-t pt-3 pb-4">
              <span className="text-sm font-semibold text-muted-foreground">Total</span>
              <motion.span
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 300, damping: 18 }}
                className="text-lg font-bold text-primary"
              >
                R${formatPrice(cart.total)}
              </motion.span>
            </div>

            {/* WhatsApp button */}
            <div className="px-5 pb-6">
              <motion.div
                animate={checkoutBtnTapped ? { scale: 0.96, opacity: 0.85 } : { scale: 1, opacity: 1 }}
                transition={{ duration: 0.12 }}
                className="relative flex items-center justify-center gap-2.5 overflow-hidden rounded-xl py-3.5 font-semibold text-white shadow-lg"
                style={{ background: "linear-gradient(135deg, #25D366 0%, #1da851 100%)" }}
              >
                {/* WhatsApp icon */}
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white shrink-0" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span>Finalizar via WhatsApp</span>

                {/* Tap ripple on button */}
                <AnimatePresence>
                  {checkoutBtnTapped && (
                    <motion.div
                      key="btn-ripple"
                      initial={{ opacity: 0.4, scale: 0 }}
                      animate={{ opacity: 0, scale: 4 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className="pointer-events-none absolute h-10 w-10 rounded-full bg-white/50"
                    />
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
